import { useEffect, useMemo, useRef } from 'react';
import { css } from 'emotion';
import { CircularProgress } from '@mui/material';
import { PDFPage } from './PDFPage';
import { usePDF } from '../../api-hooks/usePDF';
import { CustomAxios } from '../../axios/axios';
import { Div } from '../../shared-components';
import { Content } from '../brite-viewer-v2/ContentProvider';
import { useStore } from '../store-provider/use-store';
import { PDFPageV2 } from './PDFPageV2';

function useGlobalStyles(globalSettings) {
  const styleRef = useRef(null);

  // left and right margins are set at the section level
  // to account for full page width sections.
  const styleContent = `
    html {
      font-size: ${globalSettings.fontSize || 1}px;
    }
    @page {
      margin-top: ${globalSettings.margins.top}in;
      margin-bottom: ${globalSettings.margins.bottom}in;
      size: Letter;
    }
  `;

  useEffect(() => {
    if (!styleRef.current) {
      const tag = document.createElement('style');
      document.head.appendChild(tag);
      styleRef.current = tag;
    }
    styleRef.current.textContent = styleContent;
  }, [styleContent]);
}

const defaultGlobalSettings = {
  margins: {
    top: 0.5,
    right: 0.5,
    bottom: 0.5,
    left: 0.5,
  },
  fontSize: 1,
  imageSize: 1,
};

export const PDFViewer = ({ guideId, pageId }) => {
  const { pages, loading } = usePDF({ guideId });

  const globalSettings = {
    ...defaultGlobalSettings,
    ...(pages?.[0]?.GlobalSettings || {}),
  };

  useGlobalStyles(globalSettings);

  const {
    data: { displaySettings },
  } = useStore();

  // * If pageId is provided, render only that page, else render all pages.
  const pagesList = useMemo(() => {
    if (pageId) {
      return pages?.filter((page) => page.ID === pageId);
    }
    return pages;
  }, [pageId, pages, loading]);

  const pdfMetadata = JSON.stringify({
    themeLoaded: !loading,
    isReady: !loading,
  });

  if (pages?.length === 0) {
    return null;
  }

  return (
    <Div
      css={css`
        background: white;
        font-family: 'Roboto', 'Montserrat', sans-serif;
        overflow: visible;
      `}
    >
      <Div
        css={css`
          display: flex;
          flex-direction: column;
          height: 100%;
          width: 100%;
          box-sizing: border-box;
        `}
      >
        {loading ? (
          <Div
            css={css`
              width: 100%;
              height: 100%;
              display: flex;
              justify-content: center;
              align-items: center;
            `}
          >
            <CircularProgress />
          </Div>
        ) : (
          <main
            id="main-content"
            className={css`
              flex: auto;
              overflow-y: visible;
            `}
          >
            <div data-pdf-metadata={pdfMetadata} />
            <link
              rel="stylesheet"
              href={`${CustomAxios.baseURL()}public/v1/course/${pages[0]?.CourseID}/theme-css-rem`}
            />
            {pagesList?.map((page, index) => (
              <Content
                key={page?.ID}
                resources={{ displaySettings }}
                page={page}
                pageIndex={index}
                themeLoaded={true}
                type="pdf"
                globalSettings={globalSettings}
              >
                {page?.Type === 'brite-editor-v2' ? <PDFPageV2 /> : <PDFPage page={page} key={index} />}
              </Content>
            ))}
          </main>
        )}
      </Div>
    </Div>
  );
};
